import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePluginContext } from '../Plugin/index';
import { LinkchooserForm } from './forms/LinkchooserForm';
import { TabPanel, Tabs } from '../../../../../Tabs';
import { Namespace } from '../../../../../../../translation/namespace';
import { applyToEditor, createTabs, getLinkTypeForCurrentSelection } from './utilities';
import { sendLiveRegionMessage } from '../../../../../../atoms/LiveRegionGlobal/LiveRegionGlobal';
import { LoadingSpinner } from '../../../../../../atoms/LoadingSpinner';
import { getFocusableElements } from '../../../../../../../lib/DomUtils/utils';
import IForm from '../../../../../../../types/IForm';
import { LinkchooserLinkType } from '../../../../../../../types/api/LinkchooserLinkType';

export type Tab = {
  linkType: LinkchooserLinkType;
  form: IForm;
};

export function Linkchooser() {
  const [tabs, setTabs] = React.useState<Tab[]>();
  const { editor, setModalOpened, focusEditor } = usePluginContext();
  const { t } = useTranslation(Namespace.Linkchooser);

  useEffect(() => {
    createTabs(editor, setTabs);
  }, [editor]);

  const focusElementAtIndex = (elements: HTMLElement[], index: number) => {
    if (elements[index]) {
      elements[index].focus();
    }
  };

  /**
   * Focus the input element, when the underlying Tab component is rendered.
   *
   * The ModalDialog fails to set initial focus when the content is rendered
   * async. So basically the content itself would need to set focus.
   *
   * see SON-9983
   */
  const focusInput = (reference: HTMLElement) => {
    if (reference) {
      const linkType: LinkchooserLinkType = getLinkTypeForCurrentSelection(editor?.selection);
      const elements = getFocusableElements(reference);
      switch (linkType) {
        case 'page': // page has labelHelpText button, so its 2
          focusElementAtIndex(elements, 2);
          break;
        case 'person':
        case 'external':
          focusElementAtIndex(elements, 1);
          break;
        default:
          return null;
      }
    }
  };

  const handleSubmit = useCallback(
    (event: Event, model: any) => {
      event.preventDefault();
      event.stopPropagation();
      setModalOpened(false);
      applyToEditor(model.parameter, editor);
      sendLiveRegionMessage(t('linking.sucessfull'));
      focusEditor();
    },
    [editor, focusEditor, setModalOpened, t],
  );

  if (tabs) {
    return (
      <Tabs
        reference={(r: HTMLElement) => focusInput(r)}
        initialActiveTab={getLinkTypeForCurrentSelection(editor?.selection)}
      >
        {tabs.map((tab: Tab) => (
          <TabPanel id={tab.linkType} key={tab.linkType} buttonLabel={t(`${tab.linkType}.buttonLabel`)}>
            <LinkchooserForm linkType={tab.linkType} formModel={tab.form} onSubmit={handleSubmit} />
          </TabPanel>
        ))}
      </Tabs>
    );
  }
  return (
    <div className="editor__loading-spinner-container">
      <div className="editor__loading-spinner">
        <LoadingSpinner />
      </div>
    </div>
  );
}
